import axios from "axios";

import { baseUrl } from "../store/slices/auth";

export type ApplicationStatus =
  | "new"
  | "contact_required"
  | "interviewed"
  | "recommended"
  | "approved"
  | "reserve"
  | "rejected";

export type ProgramAccess =
  | "pending"
  | "active"
  | "reserve"
  | "rejected"
  | "blocked";

export type CandidateForm = {
  city: string;
  participantStatus:
    | "internally_displaced"
    | "local"
    | "veteran"
    | "military_family"
    | "other"
    | "";
  age?: number;
  education: string;
  employmentStatus: string;
  currentJob: string;
  previousJob: string;
  hasChildren: boolean;
  motivation: string;
  threeMonthGoal: string;
  communityContribution: string;
};

export type CandidateApplication = {
  _id: string;
  userId:
    | string
    | {
        _id: string;
        username: string;
        telephone: string;
        email?: string;
        role?: string;
        programAccess?: ProgramAccess;
        cohortId?: string | null;
        createdAt?: string;
      };
  status: ApplicationStatus;
  form: CandidateForm;
  adminComment?: string;
  reviewedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type Cohort = {
  _id: string;
  name: string;
  code: string;
  status: "draft" | "enrollment" | "active" | "completed" | "archived";
  startsAt: string;
  endsAt: string;
  capacity: number;
  participantIds?: Array<{
    _id: string;
    username: string;
    telephone: string;
    email?: string;
  }>;
};

export type CalendarActivity = {
  _id: string;
  cohortId: string;
  title: string;
  description: string;
  type: "offline" | "online" | "self_study" | "community" | "consultation";
  audience?: "cohort" | "selected";
  participantIds?: Array<
    | string
    | {
        _id: string;
        username: string;
        telephone: string;
        email?: string;
        role?: string;
        programAccess?: ProgramAccess;
      }
  >;
  startsAt: string;
  endsAt: string;
  location?: string;
  meetingUrl?: string;
  status: "scheduled" | "completed" | "cancelled";
  checkInOpensAt?: string | null;
  checkInClosesAt?: string | null;
  attendance?: Array<{
    userId:
      | string
      | {
          _id: string;
          username: string;
          telephone: string;
          email?: string;
        };
    present: boolean;
    markedAt?: string;
    markedBy?:
      | string
      | {
          _id: string;
          username: string;
          email?: string;
        }
      | null;
  }>;
  materials?: ProgramMaterial[];
};

export type ProgramMaterial = {
  _id: string;
  kind: "link" | "file";
  title: string;
  url: string;
  name?: string;
  mimeType?: string;
  size?: number;
  dropboxPath?: string;
  uploadedAt?: string;
};

export type ActivityAttendanceParticipant = {
  user: {
    _id: string;
    username: string;
    telephone: string;
    email?: string;
    role?: string;
    programAccess?: ProgramAccess;
  };
  present: boolean;
  markedAt?: string | null;
  markedBy?:
    | string
    | {
        _id: string;
        username: string;
        email?: string;
      }
    | null;
};

export type AdminActivityDetails = {
  activity: CalendarActivity;
  cohort: Cohort | null;
  participants: ActivityAttendanceParticipant[];
};

export type SubmissionStatus =
  | "draft"
  | "submitted"
  | "approved"
  | "revision"
  | "rejected";

export type AssignmentSubmission = {
  _id: string;
  assignmentId: string;
  userId:
    | string
    | {
        _id: string;
        username: string;
        telephone: string;
        email?: string;
      };
  answerText: string;
  answerUrl: string;
  answerData?: unknown;
  file?: ProgramMaterial | null;
  files?: ProgramMaterial[];
  status: SubmissionStatus;
  isLate: boolean;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  adminComment?: string;
  awardedPoints: number;
};

export type Assignment = {
  _id: string;
  cohortId: string | Cohort;
  title: string;
  description: string;
  weekNumber: number;
  type: "course" | "text" | "file" | "link" | "test";
  publishAt: string;
  dueAt: string;
  materialUrl?: string;
  materials?: ProgramMaterial[];
  status: "draft" | "published" | "archived";
  points: number;
  mySubmission?: AssignmentSubmission | null;
  submissionStats?: {
    total: number;
    submitted: number;
    approved: number;
    revision: number;
  };
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildMaterialsFormData = (payload: {
  files?: File[];
  links?: Array<{ title: string; url: string }>;
}) => {
  const formData = new FormData();
  formData.append("links", JSON.stringify(payload.links || []));
  (payload.files || []).forEach((file) => formData.append("files", file));
  return formData;
};

export const programApi = {
  getMyApplication: async () => {
    const response = await axios.get<{
      application: CandidateApplication;
      programAccess: ProgramAccess;
      cohortId: string | null;
    }>(`${baseUrl}applications/me`, { headers: authHeaders() });
    return response.data;
  },

  updateMyApplication: async (payload: CandidateForm) => {
    const response = await axios.put<{
      application: CandidateApplication;
    }>(`${baseUrl}applications/me`, payload, { headers: authHeaders() });
    return response.data.application;
  },

  listApplications: async (status?: ApplicationStatus) => {
    const response = await axios.get<{
      applications: CandidateApplication[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`${baseUrl}admin/applications`, {
      headers: authHeaders(),
      params: status ? { status, limit: 100 } : { limit: 100 },
    });
    return response.data;
  },

  updateApplicationStatus: async (
    id: string,
    status: Exclude<ApplicationStatus, "approved">,
    adminComment?: string
  ) => {
    const response = await axios.patch(
      `${baseUrl}admin/applications/${id}/status`,
      { status, adminComment },
      { headers: authHeaders() }
    );
    return response.data;
  },

  approveApplication: async (
    id: string,
    cohortId: string,
    adminComment?: string
  ) => {
    const response = await axios.post(
      `${baseUrl}admin/applications/${id}/approve`,
      { cohortId, adminComment },
      { headers: authHeaders() }
    );
    return response.data;
  },

  listCohorts: async () => {
    const response = await axios.get<{ cohorts: Cohort[] }>(
      `${baseUrl}admin/cohorts`,
      { headers: authHeaders() }
    );
    return response.data.cohorts;
  },

  createCohort: async (payload: {
    name: string;
    code: string;
    status: Cohort["status"];
    startsAt: string;
    endsAt: string;
    capacity: number;
  }) => {
    const response = await axios.post<{ cohort: Cohort }>(
      `${baseUrl}admin/cohorts`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.cohort;
  },

  getMyCalendar: async () => {
    const response = await axios.get<{
      cohort: Cohort;
      activities: CalendarActivity[];
      assignments: Assignment[];
    }>(`${baseUrl}calendar`, { headers: authHeaders() });
    return response.data;
  },

  getCohortCalendar: async (cohortId: string) => {
    const response = await axios.get<{
      cohort: Cohort;
      activities: CalendarActivity[];
      assignments: Assignment[];
    }>(`${baseUrl}admin/cohorts/${cohortId}/calendar`, {
      headers: authHeaders(),
    });
    return response.data;
  },

  createActivity: async (
    payload: Omit<CalendarActivity, "_id" | "status"> & {
      status?: CalendarActivity["status"];
    }
  ) => {
    const response = await axios.post<{ activity: CalendarActivity }>(
      `${baseUrl}admin/activities`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.activity;
  },

  getActivityDetails: async (id: string) => {
    const response = await axios.get<AdminActivityDetails>(
      `${baseUrl}admin/activities/${id}`,
      { headers: authHeaders() }
    );
    return response.data;
  },

  updateActivity: async (
    id: string,
    payload: Partial<
      Pick<
        CalendarActivity,
        | "title"
        | "description"
        | "type"
        | "startsAt"
        | "endsAt"
        | "location"
        | "meetingUrl"
        | "status"
        | "audience"
        | "participantIds"
      >
    >
  ) => {
    const response = await axios.patch<{ activity: CalendarActivity }>(
      `${baseUrl}admin/activities/${id}`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.activity;
  },

  updateActivityAttendance: async (id: string, presentUserIds: string[]) => {
    const response = await axios.patch<AdminActivityDetails>(
      `${baseUrl}admin/activities/${id}/attendance`,
      { presentUserIds },
      { headers: authHeaders() }
    );
    return response.data;
  },

  addActivityMaterials: async (
    id: string,
    payload: { files?: File[]; links?: Array<{ title: string; url: string }> }
  ) => {
    const response = await axios.post<{ activity: CalendarActivity }>(
      `${baseUrl}admin/activities/${id}/materials`,
      buildMaterialsFormData(payload),
      { headers: authHeaders() }
    );
    return response.data.activity;
  },

  deleteActivityMaterial: async (id: string, materialId: string) => {
    const response = await axios.delete<{ activity: CalendarActivity }>(
      `${baseUrl}admin/activities/${id}/materials/${materialId}`,
      { headers: authHeaders() }
    );
    return response.data.activity;
  },

  deleteActivity: async (id: string) => {
    await axios.delete(`${baseUrl}admin/activities/${id}`, {
      headers: authHeaders(),
    });
  },

  listMyAssignments: async (weekNumber?: number) => {
    const response = await axios.get<{ assignments: Assignment[] }>(
      `${baseUrl}assignments`,
      {
        headers: authHeaders(),
        params: weekNumber ? { weekNumber } : undefined,
      }
    );
    return response.data.assignments;
  },

  getMyAssignment: async (id: string) => {
    const response = await axios.get<{ assignment: Assignment }>(
      `${baseUrl}assignments/${id}`,
      { headers: authHeaders() }
    );
    return response.data.assignment;
  },

  submitAssignment: async (
    id: string,
    payload: {
      answerText?: string;
      answerUrl?: string;
      answerData?: unknown;
      files?: File[];
    }
  ) => {
    if (payload.files?.length) {
      const formData = new FormData();
      formData.append("answerText", payload.answerText || "");
      formData.append("answerUrl", payload.answerUrl || "");
      if (payload.answerData !== undefined) {
        formData.append("answerData", JSON.stringify(payload.answerData));
      }
      payload.files.forEach((file) => formData.append("files", file));

      const response = await axios.post<{ submission: AssignmentSubmission }>(
        `${baseUrl}assignments/${id}/submit`,
        formData,
        { headers: authHeaders() }
      );
      return response.data.submission;
    }

    const response = await axios.post<{ submission: AssignmentSubmission }>(
      `${baseUrl}assignments/${id}/submit`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.submission;
  },

  listAdminAssignments: async (cohortId?: string) => {
    const response = await axios.get<{ assignments: Assignment[] }>(
      `${baseUrl}admin/assignments`,
      {
        headers: authHeaders(),
        params: cohortId ? { cohortId } : undefined,
      }
    );
    return response.data.assignments;
  },

  createAssignment: async (payload: {
    cohortId: string;
    title: string;
    description: string;
    weekNumber: number;
    type: Assignment["type"];
    publishAt: string;
    dueAt: string;
    materialUrl?: string;
    status: Assignment["status"];
    points: number;
  }) => {
    const response = await axios.post<{ assignment: Assignment }>(
      `${baseUrl}admin/assignments`,
      payload,
      { headers: authHeaders() }
    );
    return response.data.assignment;
  },

  addAssignmentMaterials: async (
    id: string,
    payload: { files?: File[]; links?: Array<{ title: string; url: string }> }
  ) => {
    const response = await axios.post<{ assignment: Assignment }>(
      `${baseUrl}admin/assignments/${id}/materials`,
      buildMaterialsFormData(payload),
      { headers: authHeaders() }
    );
    return response.data.assignment;
  },

  deleteAssignmentMaterial: async (id: string, materialId: string) => {
    const response = await axios.delete<{ assignment: Assignment }>(
      `${baseUrl}admin/assignments/${id}/materials/${materialId}`,
      { headers: authHeaders() }
    );
    return response.data.assignment;
  },

  deleteAssignment: async (id: string) => {
    await axios.delete(`${baseUrl}admin/assignments/${id}`, {
      headers: authHeaders(),
    });
  },

  getAssignmentSubmissions: async (id: string) => {
    const response = await axios.get<{
      assignment: Assignment;
      cohort: Cohort;
      participants: Array<{
        user: {
          _id: string;
          username: string;
          telephone: string;
          email?: string;
        };
        submission: AssignmentSubmission | null;
      }>;
    }>(`${baseUrl}admin/assignments/${id}/submissions`, {
      headers: authHeaders(),
    });
    return response.data;
  },

  reviewSubmission: async (
    id: string,
    payload: {
      status: "approved" | "revision" | "rejected";
      adminComment?: string;
      awardedPoints?: number;
    }
  ) => {
    const response = await axios.patch<{
      submission: AssignmentSubmission;
    }>(`${baseUrl}admin/submissions/${id}/review`, payload, {
      headers: authHeaders(),
    });
    return response.data.submission;
  },
};
